import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgTemplateOutlet } from '@angular/common';
import { OtpauthQrComponent } from './otpauth-qr.component';

type Store = 'play' | 'apple';

interface AppLink {
  readonly name: string;
  /** Short label for the one-line computer layout ("Google", "Microsoft"). */
  readonly short: string;
  /** The app's own icon (from its store listing), served from public/assets/icons/apps. */
  readonly icon: string;
  readonly play: string;
  readonly apple: string;
}

/**
 * Authenticator setup (account setup and re-enrollment after a reset).
 * Computer: the QR code to scan with a phone, plus store links for both stores.
 * Phone (owner 2026-10-22): the parent cannot scan their own screen, so the
 * steps are: install an app from their store, tap "Add to my authenticator app"
 * (the otpauth:// link opens the app with the account) or copy the key, then
 * come back and enter the code. The QR stays one tap away for a second device.
 */
@Component({
  selector: 'app-authenticator-apps',
  standalone: true,
  imports: [NgTemplateOutlet, OtpauthQrComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (onPhone) {
      <ol class="phone-steps" data-testid="authenticator-phone-steps">
        <li>
          <strong>Get a free authenticator app</strong>, if you don't have one:
          <ng-container *ngTemplateOutlet="links" />
        </li>
        <li>
          <strong>Add your account to the app:</strong>
          <span class="phone-actions">
            @if (addLink()) {
              <a class="primary-action" [href]="addLink()" data-testid="authenticator-add-link">Add to my authenticator app</a>
            }
            @if (secret()) {
              <button type="button" class="secondary-action" (click)="copyKey()" data-testid="authenticator-copy-key">
                {{ copied() ? 'Key copied' : 'Copy the setup key' }}
              </button>
            }
          </span>
          <span class="hint">If the button doesn't open your app: copy the key, open the app, choose "Enter a setup key" and paste it.</span>
        </li>
        <li><strong>Come back here</strong> and enter the 6-digit code the app shows.</li>
      </ol>
      <button type="button" class="link-button" (click)="showQr.set(!showQr())" data-testid="authenticator-show-qr">
        {{ showQr() ? 'Hide the QR code' : 'Setting up on another device? Show the QR code' }}
      </button>
      @if (showQr()) {
        <app-otpauth-qr [otpauthUri]="otpauthUri()" />
      }
    } @else {
      <!-- Computer: the same three steps, with the QR code to scan from the phone. -->
      <ol class="phone-steps" data-testid="authenticator-apps">
        <li>
          <strong>Get a free authenticator app</strong> on your phone, if you don't have one:
          <ng-container *ngTemplateOutlet="links" />
        </li>
        <li>
          <strong>Scan this QR code</strong> with the app:
          <div class="qr-wrap"><app-otpauth-qr [otpauthUri]="otpauthUri()" /></div>
          @if (secret()) {
            <div class="manual-key" data-testid="setup-manual-key">
              QR won't scan? In the app choose "Enter a setup key" and type:
              <code>{{ secret() }}</code>
            </div>
          }
        </li>
        <li><strong>Enter the 6-digit code</strong> the app shows, below.</li>
      </ol>
    }

    <ng-template #links>
    <div class="auth-apps-list">
      @for (app of apps; track app.name) {
        <div class="auth-app">
          <span class="auth-app-name" [title]="app.name">
            <img class="auth-app-icon" [src]="app.icon" alt="" width="22" height="22" />
            {{ onPhone ? app.name : app.short }}
          </span>
          <span class="auth-app-links">
            @for (store of stores; track store) {
              <a
                class="store-link"
                [href]="store === 'play' ? app.play : app.apple"
                target="_blank"
                rel="noopener"
                [attr.data-testid]="'store-' + store + '-' + app.name"
                [attr.aria-label]="app.name + (store === 'play' ? ' on Google Play' : ' on the App Store')"
                [title]="app.name + (store === 'play' ? ' on Google Play' : ' on the App Store')"
                [class.icon-only]="!onPhone"
              >
                @if (store === 'play') {
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
                  </svg>
                  @if (onPhone) { Google Play }
                } @else {
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
                  </svg>
                  @if (onPhone) { App Store }
                }
              </a>
            }
          </span>
        </div>
      }
    </div>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      /* Numbered circles (the portal's global styles remove list numbers). */
      .phone-steps {
        list-style: none;
        counter-reset: step;
        margin: 8px auto 0;
        padding-left: 0;
        max-width: 420px;
        text-align: left;
        color: var(--gp-ink);
        font-size: 14px;
        line-height: 20px;
      }
      .phone-steps li {
        position: relative;
        counter-increment: step;
        margin-bottom: 16px;
        padding-left: 34px;
      }
      .phone-steps li::before {
        content: counter(step);
        position: absolute;
        left: 0;
        top: -1px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: var(--gp-primary);
        color: var(--gp-primary-ink);
        font-size: 13px;
        font-weight: 800;
        line-height: 24px;
        text-align: center;
      }
      .phone-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 8px 0 6px;
      }
      .primary-action {
        display: inline-block;
        padding: 10px 16px;
        border-radius: 999px;
        background: var(--gp-primary);
        color: var(--gp-primary-ink);
        font-weight: 800;
        text-decoration: none;
      }
      .secondary-action,
      .link-button {
        border: 1.5px solid var(--gp-border);
        background: var(--gp-surface);
        color: var(--gp-ink);
        border-radius: 999px;
        padding: 9px 14px;
        font: inherit;
        font-weight: 700;
        cursor: pointer;
      }
      .link-button {
        display: block;
        margin: 4px auto 0;
        border: 0;
        background: none;
        color: var(--gp-primary);
        font-size: 13px;
      }
      .hint {
        display: block;
        font-size: 12px;
        color: var(--gp-ink-muted);
      }
      .qr-wrap {
        display: flex;
        justify-content: center;
        margin: 10px 0 8px -34px; /* centred on the card, not the indented step text */
      }
      .manual-key {
        font-size: 12px;
        color: var(--gp-ink-muted);
        text-align: center;
      }
      .manual-key code {
        display: block;
        margin-top: 4px;
        font-size: 15px;
        letter-spacing: 1px;
        color: var(--gp-ink);
        word-break: break-all;
      }
      .qr-hint {
        margin: 8px 0 0;
        font-size: 13px;
        color: var(--gp-ink-muted);
        text-align: center;
      }
      .auth-apps-list {
        margin-top: 6px;
      }
      .auth-apps {
        margin: 12px auto 0;
        max-width: 420px;
        text-align: left;
      }
      .auth-apps-title {
        margin: 0 0 8px;
        font-size: 13px;
        font-weight: 700;
        color: var(--gp-ink-muted);
        text-align: center;
      }
      /* owner 2026-10-22: one line at most — each app is icon + name + its store buttons. */
      .auth-apps-list {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px 18px;
        margin-top: 8px;
      }
      .auth-app {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .auth-app-icon {
        width: 22px;
        height: 22px;
        border-radius: 5px;
      }
      .auth-app-name {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 700;
        color: var(--gp-ink);
        white-space: nowrap;
      }
      .auth-app-links {
        display: inline-flex;
        gap: 4px;
      }
      .store-link.icon-only {
        width: 30px;
        height: 30px;
        padding: 0;
        justify-content: center;
      }
      .store-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        border-radius: 999px;
        border: 1.5px solid var(--gp-border);
        background: var(--gp-surface);
        color: var(--gp-ink);
        font-size: 12px;
        font-weight: 700;
        text-decoration: none;
        white-space: nowrap;
      }
      .store-link:hover {
        border-color: var(--gp-primary);
        color: var(--gp-primary);
      }
    `,
  ],
})
export class AuthenticatorAppsComponent {
  private readonly sanitizer = inject(DomSanitizer);

  /** The otpauth:// URI from /mfa/enroll (component state only, never logged). */
  readonly otpauthUri = input.required<string>();

  readonly showQr = signal(false);
  readonly copied = signal(false);

  /** Only a well-formed TOTP URI becomes a link (Angular would otherwise mark the scheme unsafe). */
  readonly addLink = computed<SafeUrl | null>(() => {
    const uri = this.otpauthUri();
    return uri.startsWith('otpauth://totp/') ? this.sanitizer.bypassSecurityTrustUrl(uri) : null;
  });

  readonly secret = computed(() => secretOf(this.otpauthUri()));

  async copyKey(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.secret());
      this.copied.set(true);
    } catch {
      this.copied.set(false);
    }
  }

  readonly apps: readonly AppLink[] = [
    {
      name: 'Google Authenticator',
      short: 'Google',
      icon: 'assets/icons/apps/google-authenticator.png',
      play: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
      apple: 'https://apps.apple.com/app/google-authenticator/id388497605',
    },
    {
      name: 'Microsoft Authenticator',
      short: 'Microsoft',
      icon: 'assets/icons/apps/microsoft-authenticator.png',
      play: 'https://play.google.com/store/apps/details?id=com.azure.authenticator',
      apple: 'https://apps.apple.com/app/microsoft-authenticator/id983156458',
    },
  ];

  /** A phone sees its own store only; a computer (or an unknown device) sees both. */
  readonly stores: readonly Store[] = storesFor(typeof navigator === 'undefined' ? '' : navigator.userAgent);

  /** iPhone / Android: the step-by-step instead of the QR code. */
  readonly onPhone = this.stores.length === 1;
}

export function secretOf(otpauthUri: string): string {
  const query = otpauthUri.split('?')[1] ?? '';
  return new URLSearchParams(query).get('secret') ?? '';
}

export function storesFor(userAgent: string): readonly Store[] {
  if (/iPhone|iPad|iPod/.test(userAgent)) return ['apple'];
  if (/Android/.test(userAgent)) return ['play'];
  return ['play', 'apple'];
}
